import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { fetchPublicGallery } from '../utils/api'
import BeforeAfterSlider from './BeforeAfterSlider'

const GallerySection = () => {
  const [galleryItems, setGalleryItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [activeCategory, setActiveCategory] = useState('all')

  const categories = ['all', 'Cleanup', 'Heritage', 'Community', 'Photography', 'Impact', 'Youth', 'Environment', 'Events']

  useEffect(() => {
    const loadGallery = async () => {
      try {
        const res = await fetchPublicGallery()
        setGalleryItems(res.data?.data || [])
      } catch (error) {
        console.error('Error loading gallery:', error)
      } finally {
        setLoading(false)
      }
    }
    loadGallery()
  }, [])

  const filteredItems = activeCategory === 'all' 
    ? galleryItems 
    : galleryItems.filter(item => item.tag === activeCategory)

  return (
    <section id="gallery" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <div className="inline-block px-4 py-1.5 rounded-full border border-accent-maroon/30 bg-accent-maroon/10 backdrop-blur-md mb-4">
            <span className="text-accent-maroon text-sm font-semibold tracking-widest uppercase">Visual Impact</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-black font-grotesk mb-6">
            <span className="bg-gradient-to-r from-accent-maroon to-accent-maroonDark bg-clip-text text-transparent">
              Gallery Experience
            </span>
          </h2>
          <p className="text-gray-600 text-xl max-w-2xl mx-auto">
            Witness the incredible transformation of Udaipur's heritage and environment through our community efforts.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="flex flex-wrap justify-center gap-4 mb-16">
          {categories.map((cat) => (
            <motion.button
              key={cat}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setActiveCategory(cat)}
              className={`px-8 py-2.5 rounded-full font-bold transition-all duration-300 ${
                activeCategory === cat
                  ? 'bg-accent-maroon text-white shadow-[0_0_20px_rgba(128,0,0,0.3)]'
                  : 'bg-white/10 text-gray-600 border border-gray-200 hover:border-accent-maroon/50 hover:text-gray-800'
              }`}
            >
              {cat}
            </motion.button>
          ))}
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-12 h-12 border-4 border-accent-maroon border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : (
          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            <AnimatePresence mode='popLayout'>
              {filteredItems.map((item) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                >
                  <BeforeAfterSlider before={item.before_image_url} after={item.after_image_url} title={item.title} />
                  <div className="mt-4 flex items-center justify-between px-2">
                    <p className="text-gray-600 text-sm line-clamp-1 flex-1 mr-4">{item.description}</p>
                    <span className="px-3 py-1 bg-white/10 border border-gray-200 rounded-full text-xs text-accent-maroon font-semibold">
                      {item.tag}
                    </span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {!loading && filteredItems.length === 0 && (
          <div className="text-center py-20 bg-white/10 rounded-2xl border border-dashed border-gray-200">
            <p className="text-gray-600 text-lg">No gallery items found in this category.</p>
          </div>
        )}
      </div>
    </section>
  )
}

export default GallerySection